import { useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type GapReason, explainGaps } from "@/api/planning";

type Props = {
  open: boolean;
  organizationId: string;
  year: number;
  month: number;
  onClose: () => void;
};

type ReasonCode = GapReason["reason"];

const REASON_COLOR: Record<ReasonCode, "default" | "warning" | "error" | "info"> = {
  busy_same_date: "info",
  unavailable_pref: "warning",
  on_time_off: "error",
  missing_skill: "default",
};

export function ExplainGapsDialog({
  open,
  organizationId,
  year,
  month,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const explain = useQuery({
    queryKey: ["explain-gaps", organizationId, year, month],
    queryFn: () => explainGaps(organizationId, year, month),
    enabled: open && Boolean(organizationId),
  });

  const rows = useMemo(() => {
    if (!explain.data) return [];
    return explain.data.cells
      .filter((c) => c.free_candidates === 0 || c.blocked.length > 0)
      .map((c) => {
        const counts: Record<ReasonCode, number> = {
          busy_same_date: 0,
          unavailable_pref: 0,
          on_time_off: 0,
          missing_skill: 0,
        };
        for (const b of c.blocked) counts[b.reason] += 1;
        return { ...c, counts };
      })
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [explain.data]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>{t("explainGaps.title")}</span>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t("explainGaps.subtitle")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {explain.isLoading && <CircularProgress />}
        {explain.isError && (
          <Alert severity="error">{(explain.error as Error).message}</Alert>
        )}
        {explain.data && rows.length === 0 && (
          <Alert severity="success">{t("explainGaps.allCovered")}</Alert>
        )}
        {rows.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("explainGaps.date")}</TableCell>
                  <TableCell>{t("explainGaps.slot")}</TableCell>
                  <TableCell align="right">{t("explainGaps.candidates")}</TableCell>
                  <TableCell align="right">{t("explainGaps.free")}</TableCell>
                  <TableCell>{t("explainGaps.reasons")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={`${c.date}-${c.slot_template_id}`}>
                    <TableCell sx={{ fontFamily: "monospace" }}>{c.date}</TableCell>
                    <TableCell>{c.slot_label}</TableCell>
                    <TableCell align="right">{c.candidates_total}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={c.free_candidates}
                        color={c.free_candidates === 0 ? "error" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(Object.entries(c.counts) as [ReasonCode, number][])
                          .filter(([, n]) => n > 0)
                          .map(([k, n]) => (
                            <Chip
                              key={k}
                              size="small"
                              variant="outlined"
                              color={REASON_COLOR[k]}
                              label={`${t(`explainGaps.reason.${k}`)} · ${n}`}
                            />
                          ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
